import type { FC, ReactElement } from 'react';
import { memo, useMemo } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as Icons from '@iconscout/react-unicons';
import type { Route } from '../../bootstrap/router/hooks';
import { Text, TextLink, Title } from '../../bootstrap/typography';
import { ReactComponent as LogoTM2 } from './images/tm2-logo.svg';
import { ReactComponent as LogoTelemed } from './images/telemed-logo.svg';
import { ReactComponent as LogoSolveva } from './images/solveva-logo.svg';
// import { ReactComponent as LogoDarkPool } from './images/darkpool-logo.svg';
import s from './index.module.scss';

type ProjectCellProps = {
  icon?: ReactElement;
  title: string;
  text: string;
  href?: string;
};
const ProjectCell: FC<ProjectCellProps> = memo(
  ({ icon = <Icons.UilClock width="40px" height="40px" color="#555" />, title, text, href }) => {
    return (
      <a href={href} className={s.projectCell} target="_blank" rel="noreferrer">
        <i className={s.icon}>{icon}</i>
        <div className={s.projectInfo}>
          <Text size="1" color="3" className={s.projectInfoTitle}>
            {title}
          </Text>
          <Text size="2" color="3" className={s.projectInfoText}>
            {text}
          </Text>
        </div>
      </a>
    );
  }
);

export const ProjectsPage: FC = memo(() => {
  return (
    <div className={s.page}>
      <Title size={1} color={1} className={s.title}>
        Projects
      </Title>
      <Text size="1" color="3" className={s.proudOf}>
        List of projects I have participated in
      </Text>
      <Title size={2} color={3} className={s.subTitle}>
        Commercial
      </Title>
      <div className={s.projectsList}>
        <ProjectCell
          icon={<LogoSolveva width="40px" height="40px" />}
          title="Solveva"
          text="Modeling and pricing suite tools for insurance companies"
          href="https://www.solveva.com/"
        />
        <ProjectCell
          icon={<LogoTM2 width="40px" height="40px" />}
          title="Technology Metals Market"
          text="The unique platform for trading rare earth metals"
          href="https://tm2.com/"
        />
        <ProjectCell
          icon={<LogoTelemed width="40px" height="40px" />}
          title="Telemed Technology"
          text="Technology tools and services to enable telehealth as a mode of care"
          href="https://telemed.technology/"
        />
        {/* <ProjectCell */}
        {/*   icon={<LogoDarkPool width="40px" height="40px" />} */}
        {/*   title="Dark Pool" */}
        {/*   text="A blockchain-based platform providing anonymous sports betting" */}
        {/* /> */}
      </div>

      <Title size={2} color={3} className={s.subTitle}>
        Latest Pet Projects
      </Title>
      <div className={s.projectsList}>
        <ProjectCell
          title="Zentro (NEW)"
          text="A modern banking dashboard and transactions application using Next.js"
          href="https://github.com/gzhel/zentro"
          icon={<Icons.UilUsdCircle width="40px" height="40px" color="#555" />}
        />
        <ProjectCell
          title="Pokedex"
          text="A modern SSR application with a list of Pokemon cards using Next.js"
          href="https://github.com/gzhel/pokedex"
          icon={<Icons.UilCompactDisc width="40px" height="40px" color="#555" />}
        />
        <ProjectCell
          title="Devfinder"
          text="An application that allows you to find user information through the GitHub API"
          href="https://github.com/gzhel/devfinder"
          icon={<Icons.UilGithub width="40px" height="40px" color="#555" />}
        />
        <ProjectCell
          title="Sneakers"
          text="Dynamic page with a preview of the product and adding it to the cart"
          href="https://github.com/gzhel/sneakers"
          icon={<Icons.UilShoppingCart width="40px" height="40px" color="#555" />}
        />
        <ProjectCell
          title="NodeAuth"
          text="Authorization in the application using JWT and confirmation via mail"
          href="https://github.com/gzhel/nodeauth"
          icon={<Icons.UilShieldCheck width="40px" height="40px" color="#555" />}
        />
        <ProjectCell
          title="EvilAuth"
          text="Custom frontend authorization form via react-hook-form library"
          href="https://github.com/gzhel/evil-auth"
          icon={<Icons.UilAndroidAlt width="40px" height="40px" color="#555" />}
        />
      </div>

      <Text size="1" color="3" className={s.thanks}>
        Thank you for being interested in my projects! If you like them or find them interesting,
        you can{' '}
        <TextLink size="1" color="1" className={s.link} href="mailto:gregoryzhel@gmail.com">
          email me
        </TextLink>{' '}
        for collaboration or further cooperation. See you :)
      </Text>
      <TextLink size="1" color="3" className={s.cd} to="/">
        cd ..
      </TextLink>
    </div>
  );
});

export const useProjectsRoute = () => {
  const route = useMemo<Route>(
    () => ({
      path: '/projects',
      element: ProjectsPage
    }),
    []
  );

  return { route };
};
